import { roleCategoryOptions } from '@/data/options';
import type { GuildState } from '@/types/app-state';
import type {
  EventBuilderDraft,
  EventBuilderPartyDraft,
  EventBuilderSlotDraft,
  EventMass,
  PartyTemplateSlot,
  RoleCategory,
} from '@/types/domain';

const roleCategoryRankMap = new Map<RoleCategory, number>(roleCategoryOptions.map((roleCategory, index) => [roleCategory, index]));

function createClientId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function createEmptySlotDraft(partyNumber: number, slotOrder: number): EventBuilderSlotDraft {
  return {
    clientId: createClientId(),
    partyNumber,
    slotOrder,
    note: '',
    roleCategory: 'DPS',
    weaponName: '',
    imagePath: '',
    capacity: 1,
    waitlistEnabled: true,
    isActive: true,
  };
}

export function getRoleCategoryRank(roleCategory: RoleCategory): number {
  return roleCategoryRankMap.get(roleCategory) ?? Number.MAX_SAFE_INTEGER;
}

export function sortPartySlotsByRoleOrder(slots: EventBuilderSlotDraft[]): EventBuilderSlotDraft[] {
  return slots
    .map((slot, index) => ({ slot, index }))
    .sort((left, right) => {
      const roleDifference = getRoleCategoryRank(left.slot.roleCategory) - getRoleCategoryRank(right.slot.roleCategory);

      if (roleDifference !== 0) {
        return roleDifference;
      }

      const slotOrderDifference = left.slot.slotOrder - right.slot.slotOrder;

      if (slotOrderDifference !== 0) {
        return slotOrderDifference;
      }

      return left.index - right.index;
    })
    .map(({ slot }) => slot);
}

function findSiblingIndexWithinRoleGroup(
  slots: EventBuilderSlotDraft[],
  clientId: string,
  direction: 'up' | 'down',
): number {
  const currentIndex = slots.findIndex((slot) => slot.clientId === clientId);

  if (currentIndex === -1) {
    return -1;
  }

  const delta = direction === 'up' ? -1 : 1;
  const currentRole = slots[currentIndex]?.roleCategory;

  for (let index = currentIndex + delta; index >= 0 && index < slots.length; index += delta) {
    if (slots[index]?.roleCategory === currentRole) {
      return index;
    }

    if (getRoleCategoryRank(slots[index]?.roleCategory ?? currentRole) !== getRoleCategoryRank(currentRole)) {
      break;
    }
  }

  return -1;
}

export function canMoveSlotWithinRoleGroup(
  slots: EventBuilderSlotDraft[],
  clientId: string,
  direction: 'up' | 'down',
): boolean {
  return findSiblingIndexWithinRoleGroup(sortPartySlotsByRoleOrder(slots), clientId, direction) !== -1;
}

export function moveSlotWithinRoleGroup(
  slots: EventBuilderSlotDraft[],
  clientId: string,
  direction: 'up' | 'down',
): EventBuilderSlotDraft[] {
  const sortedSlots = sortPartySlotsByRoleOrder(slots);
  const currentIndex = sortedSlots.findIndex((slot) => slot.clientId === clientId);
  const siblingIndex = findSiblingIndexWithinRoleGroup(sortedSlots, clientId, direction);

  if (currentIndex === -1 || siblingIndex === -1) {
    return normalizePartySlots(sortedSlots);
  }

  const currentOrder = sortedSlots[currentIndex]?.slotOrder ?? currentIndex + 1;
  const siblingOrder = sortedSlots[siblingIndex]?.slotOrder ?? siblingIndex + 1;
  const nextSlots = sortedSlots.map((slot, index) => {
    if (index === currentIndex) {
      return {
        ...slot,
        slotOrder: siblingOrder,
      };
    }

    if (index === siblingIndex) {
      return {
        ...slot,
        slotOrder: currentOrder,
      };
    }

    return slot;
  });

  return normalizePartySlots(nextSlots);
}

export function createEmptyPartyDrafts(): EventBuilderPartyDraft[] {
  return Array.from({ length: 4 }, (_, index) => ({
    partyNumber: index + 1,
    label: `Party ${index + 1}`,
    sortOrder: index + 1,
    slots: [],
  }));
}

export function createDefaultEventDraft(): EventBuilderDraft {
  const now = new Date();
  const opensAt = new Date(now.getTime() + 60 * 60 * 1000);
  const closesAt = new Date(now.getTime() + 4 * 60 * 60 * 1000);
  const startsAt = new Date(now.getTime() + 6 * 60 * 60 * 1000);

  return {
    title: '',
    description: '',
    status: 'draft',
    startsAt: startsAt.toISOString(),
    signupOpensAt: opensAt.toISOString(),
    signupClosesAt: closesAt.toISOString(),
    waitlistEnabled: true,
    parties: createEmptyPartyDrafts(),
  };
}

function mapTemplateSlotToDraft(slot: PartyTemplateSlot): EventBuilderSlotDraft {
  return {
    clientId: createClientId(),
    templateSlotId: slot.id,
    partyNumber: slot.partyNumber,
    slotOrder: slot.slotOrder,
    note: slot.note ?? '',
    roleCategory: slot.roleCategory,
    weaponId: slot.weaponId,
    weaponName: slot.weaponName,
    imagePath: slot.imagePath,
    capacity: slot.capacity,
    waitlistEnabled: slot.waitlistEnabled,
    isActive: slot.isActive,
  };
}

export function cloneTemplateSlotsToParties(templateId: string, state: GuildState): EventBuilderPartyDraft[] {
  const template = state.templates.find((entry) => entry.id === templateId);
  const templateSlots = state.templateSlots
    .filter((slot) => slot.templateId === templateId)
    .sort((left, right) => left.partyNumber - right.partyNumber || left.slotOrder - right.slotOrder);

  const parties = createEmptyPartyDrafts().map((party) => {
    const savedParty = template?.partyLabels.find((entry) => entry.partyNumber === party.partyNumber);

    return savedParty
      ? {
          ...party,
          label: savedParty.label,
          sortOrder: savedParty.sortOrder,
        }
      : party;
  });

  for (const party of parties) {
    party.slots = normalizePartySlots(
      templateSlots
        .filter((slot) => slot.partyNumber === party.partyNumber)
        .map((slot) => mapTemplateSlotToDraft(slot)),
    );
  }

  return parties;
}

function mapRoleCategory(value: string): RoleCategory {
  return value as RoleCategory;
}

export function createEventDraftFromState(state: GuildState, event: EventMass): EventBuilderDraft {
  const parties = state.eventParties
    .filter((party) => party.eventId === event.id)
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map<EventBuilderPartyDraft>((party) => ({
      id: party.id,
      partyNumber: party.partyNumber,
      label: party.label,
      sortOrder: party.sortOrder,
      slots: normalizePartySlots(
        state.eventSlots
          .filter((slot) => slot.eventPartyId === party.id)
          .sort((left, right) => left.slotOrder - right.slotOrder)
          .map((slot) => ({
            clientId: createClientId(),
            id: slot.id,
            templateSlotId: slot.templateSlotId,
            partyNumber: slot.partyNumber,
            slotOrder: slot.slotOrder,
            note: slot.note ?? (slot.roleName !== slot.roleCategory ? slot.roleName : ''),
          roleCategory: mapRoleCategory(slot.roleCategory),
          weaponId: slot.weaponId,
          weaponName: slot.weaponName,
          imagePath: slot.imagePath,
          capacity: slot.capacity,
          waitlistEnabled: slot.waitlistEnabled,
          isActive: slot.isActive,
          })),
      ),
    }));

  return {
    id: event.id,
    templateId: event.templateId,
    title: event.title,
    description: event.description,
    status: event.status,
    startsAt: event.startsAt,
    signupOpensAt: event.signupOpensAt,
    signupClosesAt: event.signupClosesAt,
    waitlistEnabled: event.waitlistEnabled,
    parties: parties.length > 0 ? parties : createEmptyPartyDrafts(),
  };
}

export function addSlotToParty(draft: EventBuilderDraft, partyNumber: number): EventBuilderDraft {
  return {
    ...draft,
    parties: draft.parties.map((party) =>
      party.partyNumber === partyNumber
        ? {
            ...party,
            slots: normalizePartySlots([...party.slots, createEmptySlotDraft(partyNumber, party.slots.length + 1)]),
          }
        : party,
    ),
  };
}

export function normalizePartySlots(slots: EventBuilderSlotDraft[]): EventBuilderSlotDraft[] {
  return sortPartySlotsByRoleOrder(slots).map((slot, index) => ({
    ...slot,
    slotOrder: index + 1,
  }));
}
